import React, { useState, useRef, Component } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, DownloadIcon, UsersIcon, ClockIcon, AwardIcon, CheckIcon, GraduationCapIcon, ChevronDownIcon } from 'lucide-react';
import { Footer } from '../components/Footer';
import { programs } from './courseData';
import { navigate } from '../utils/navigate';
import { MAILTO } from '../utils/mailto';
/* ─── Helpers ─── */
function FadeIn({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <motion.div ref={ref} className={className} initial={{
    opacity: 0,
    y: 20
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.55,
    delay,
    ease: [0.22, 1, 0.36, 1]
  }} data-id="element-1489">
      {children}
    </motion.div>;
}
const levelColors: Record<string, string> = {
  Foundational: 'text-blue-700 bg-blue-50 border-blue-200',
  Intermediate: 'text-amber-700 bg-amber-50 border-amber-200',
  Advanced: 'text-red-700 bg-red-50 border-red-200',
  Executive: 'text-purple-700 bg-purple-50 border-purple-200'
};
/* ─── Main Component ─── */
export function CourseDetail() {
  const path = window.location.pathname.replace(/^\//, '');
  const program = programs.find(p => p.route === path);
  const [activeTab, setActiveTab] = useState<'overview' | 'modules' | 'delivery'>('overview');
  const [expandedModule, setExpandedModule] = useState<number | null>(null);
  if (!program) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FAFDF7]" data-id="element-1490">
        <div className="text-center" data-id="element-1491">
          <p className="text-carbon-deep font-bold mb-4" data-id="element-1492">Program not found.</p>
          <button onClick={() => navigate('courses')} className="text-sm text-carbon-bright hover:underline" data-id="element-1493">
            ← Back to all programs
          </button>
        </div>
      </div>;
  }
  const tabs = [{
    id: 'overview',
    label: 'Overview & Outcomes'
  }, {
    id: 'modules',
    label: 'Module Breakdown'
  }, {
    id: 'delivery',
    label: 'Delivery & Certification'
  }] as const;
  return <>
      {/* ── HERO — light bg matching DMRVHero ── */}
      <section className="relative w-full overflow-hidden bg-[#FAFDF7]" data-id="element-1494">
        <svg className="absolute inset-0 w-full h-full opacity-[0.04]" aria-hidden="true" data-id="element-1495">
          <defs data-id="element-1496">
            <pattern id="course-grid" width="50" height="50" patternUnits="userSpaceOnUse" patternTransform="rotate(45)" data-id="element-1497">
              <line x1="0" y1="0" x2="0" y2="50" stroke="#1B4332" strokeWidth="0.5" data-id="element-1498" />
              <line x1="0" y1="0" x2="50" y2="0" stroke="#1B4332" strokeWidth="0.5" data-id="element-1499" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#course-grid)" data-id="element-1500" />
        </svg>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28" data-id="element-1501">
          {/* Breadcrumb */}
          <motion.button initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.4
        }} onClick={() => navigate('courses')} className="flex items-center gap-2 text-sm font-semibold text-carbon-mid hover:text-carbon-bright transition-colors mb-8" data-id="element-1502">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-1503" />
            All Programs
          </motion.button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start" data-id="element-1504">
            <motion.div initial={{
            opacity: 0,
            x: -24
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.65,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-1505">
              <div className="flex items-center gap-3 mb-5" data-id="element-1506">
                <div className="w-12 h-12 rounded-xl bg-carbon-bright/10 flex items-center justify-center" data-id="element-1507">
                  <div className="text-carbon-bright" data-id="element-1508">{program.icon}</div>
                </div>
                <div data-id="element-1509">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-carbon-bright" data-id="element-1510">
                    {program.category}
                  </p>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 border rounded-full ${levelColors[program.level]}`} data-id="element-1511">
                    {program.level}
                  </span>
                </div>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-carbon-deep tracking-tight leading-[1.1]" data-id="element-1512">
                {program.title}
              </h1>
              <p className="mt-5 text-lg text-carbon-mid leading-relaxed max-w-xl" data-id="element-1513">
                {program.description}
              </p>

              <div className="mt-6 flex flex-wrap gap-2" data-id="element-1514">
                {program.regulatoryAlignment.map(r => <span key={r} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-carbon-geo bg-white text-sm font-medium text-carbon-deep" data-id="element-1515">
                    <span className="w-2 h-2 rounded-full bg-carbon-bright" data-id="element-1516" />
                    {r}
                  </span>)}
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-4" data-id="element-1517">
                <a href={MAILTO.enrollCourse} className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-carbon-bright text-white text-base font-semibold rounded-lg hover:bg-carbon-light transition-colors shadow-lg shadow-carbon-bright/20" data-id="element-1518">
                  Enroll Now <ArrowRightIcon className="w-4 h-4" data-id="element-1519" />
                </a>
                <a href={MAILTO.courseSyllabus} className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border-2 border-carbon-bright text-carbon-deep text-base font-semibold rounded-lg hover:bg-carbon-bright/5 transition-colors" data-id="element-1520">
                  <DownloadIcon className="w-4 h-4" data-id="element-1521" /> Download Syllabus
                </a>
              </div>
            </motion.div>

            {/* Right: quick stats — light cards */}
            <motion.div initial={{
            opacity: 0,
            x: 24
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.65,
            delay: 0.15,
            ease: [0.22, 1, 0.36, 1]
          }} className="grid grid-cols-2 gap-4" data-id="element-1522">
              {[{
              label: 'Duration',
              value: program.duration
            }, {
              label: 'Level',
              value: program.level
            }, {
              label: 'CPD Hours',
              value: `${program.cpdHours} hrs`
            }, {
              label: 'Price',
              value: `$${program.price.toLocaleString()}`
            }, {
              label: 'Modules',
              value: `${program.modules.length} modules`
            }, {
              label: 'Corporate Discount',
              value: '5+ seats'
            }].map(stat => <div key={stat.label} className="rounded-2xl border border-carbon-geo bg-white px-5 py-4 shadow-sm" data-id="element-1523">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-carbon-mid/60 mb-1" data-id="element-1524">
                    {stat.label}
                  </p>
                  <p className={`text-xl font-extrabold ${stat.label === 'Price' ? 'text-carbon-bright' : 'text-carbon-deep'}`} data-id="element-1525">
                    {stat.value}
                  </p>
                </div>)}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── MAIN CONTENT ── */}
      <section className="w-full bg-white py-16 sm:py-20 border-t border-carbon-geo" data-id="element-1526">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1527">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-start" data-id="element-1528">
            {/* Left: Tabbed content */}
            <div className="lg:col-span-2" data-id="element-1529">
              <div className="flex border-b border-carbon-geo mb-8" data-id="element-1530">
                {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors -mb-px ${activeTab === tab.id ? 'border-carbon-bright text-carbon-deep' : 'border-transparent text-carbon-mid hover:text-carbon-deep'}`} data-id="element-1531">
                    {tab.label}
                  </button>)}
              </div>

              {/* Overview */}
              {activeTab === 'overview' && <motion.div key="overview" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3
            }} data-id="element-1532">
                  <div className="mb-8" data-id="element-1533">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-carbon-mid/60 mb-4" data-id="element-1534">
                      Who This Is For
                    </h3>
                    <ul className="space-y-2" data-id="element-1535">
                      {program.audienceList.map((a, i) => <li key={i} className="flex items-start gap-3" data-id="element-1536">
                          <div className="w-5 h-5 rounded-lg bg-carbon-bright/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-1537">
                            <CheckIcon className="w-3 h-3 text-carbon-bright" strokeWidth={3} data-id="element-1538" />
                          </div>
                          <span className="text-base text-carbon-mid" data-id="element-1539">{a}</span>
                        </li>)}
                    </ul>
                  </div>

                  <div className="mb-8" data-id="element-1540">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-carbon-mid/60 mb-4" data-id="element-1541">
                      Regulatory Alignment
                    </h3>
                    <div className="flex flex-wrap gap-2" data-id="element-1542">
                      {program.regulatoryAlignment.map(r => <span key={r} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-carbon-geo bg-[#FAFDF7] text-sm font-medium text-carbon-deep" data-id="element-1543">
                          <span className="w-2 h-2 rounded-full bg-carbon-bright" data-id="element-1544" />
                          {r}
                        </span>)}
                    </div>
                  </div>

                  <div className="mb-8" data-id="element-1545">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-carbon-mid/60 mb-4" data-id="element-1546">
                      Learning Outcomes
                    </h3>
                    <ul className="space-y-3" data-id="element-1547">
                      {program.learningOutcomes.map((outcome, i) => <li key={i} className="flex items-start gap-3" data-id="element-1548">
                          <div className="w-5 h-5 rounded-lg bg-carbon-bright/15 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-1549">
                            <CheckIcon className="w-3 h-3 text-carbon-bright" strokeWidth={3} data-id="element-1550" />
                          </div>
                          <span className="text-base text-carbon-mid leading-snug" data-id="element-1551">
                            {outcome}
                          </span>
                        </li>)}
                    </ul>
                  </div>

                  <div data-id="element-1552">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-carbon-mid/60 mb-4" data-id="element-1553">
                      Case Studies
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1554">
                      {program.caseStudies.map((cs, i) => <div key={i} className="rounded-2xl border border-carbon-geo p-5 bg-[#FAFDF7]" data-id="element-1555">
                          <p className="text-sm font-bold text-carbon-deep mb-1" data-id="element-1556">
                            {cs.title}
                          </p>
                          <p className="text-sm text-carbon-mid leading-snug" data-id="element-1557">
                            {cs.desc}
                          </p>
                        </div>)}
                    </div>
                  </div>
                </motion.div>}

              {/* Modules */}
              {activeTab === 'modules' && <motion.div key="modules" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3
            }} data-id="element-1558">
                  <div className="space-y-3" data-id="element-1559">
                    {program.modules.map((mod, i) => <div key={i} className="rounded-2xl border border-carbon-geo bg-[#FAFDF7] overflow-hidden" data-id="element-1560">
                        <button onClick={() => setExpandedModule(expandedModule === i ? null : i)} className="flex items-center gap-4 w-full p-5 text-left" data-id="element-1561">
                          <span className="text-xl font-black text-carbon-geo leading-none flex-shrink-0 w-7" data-id="element-1562">
                            {String(i + 1).padStart(2, '0')}
                          </span>
                          <div className="flex-1 min-w-0" data-id="element-1563">
                            <p className="text-sm font-bold text-carbon-deep" data-id="element-1564">
                              {mod.title}
                            </p>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0" data-id="element-1565">
                            <span className="text-[10px] font-semibold text-carbon-mid/60 flex items-center gap-1" data-id="element-1566">
                              <ClockIcon className="w-3 h-3" data-id="element-1567" />
                              {mod.duration}
                            </span>
                            <ChevronDownIcon className={`w-4 h-4 text-carbon-mid/50 transition-transform duration-200 ${expandedModule === i ? 'rotate-180' : ''}`} data-id="element-1568" />
                          </div>
                        </button>
                        {expandedModule === i && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} transition={{
                    duration: 0.25
                  }} className="overflow-hidden" data-id="element-1569">
                            <ul className="px-5 pb-5 space-y-2 border-t border-carbon-geo pt-4" data-id="element-1570">
                              {mod.topics.map((topic, j) => <li key={j} className="flex items-start gap-2" data-id="element-1571">
                                  <CheckIcon className="w-3.5 h-3.5 text-carbon-bright mt-0.5 flex-shrink-0" data-id="element-1572" />
                                  <span className="text-sm text-carbon-mid" data-id="element-1573">
                                    {topic}
                                  </span>
                                </li>)}
                            </ul>
                          </motion.div>}
                      </div>)}
                  </div>
                </motion.div>}

              {/* Delivery */}
              {activeTab === 'delivery' && <motion.div key="delivery" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3
            }} data-id="element-1574">
                  <div className="space-y-8" data-id="element-1575">
                    <div data-id="element-1576">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-carbon-mid/60 mb-4" data-id="element-1577">
                        Delivery Formats
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-1578">
                        {program.deliveryFormats.map(format => <div key={format.label} className="rounded-2xl border border-carbon-geo p-5 bg-[#FAFDF7]" data-id="element-1579">
                            <p className="text-sm font-bold text-carbon-deep mb-2" data-id="element-1580">
                              {format.label}
                            </p>
                            <p className="text-sm text-carbon-mid leading-relaxed" data-id="element-1581">
                              {format.desc}
                            </p>
                          </div>)}
                      </div>
                    </div>

                    <div data-id="element-1582">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-carbon-mid/60 mb-4" data-id="element-1583">
                        Certification Details
                      </h3>
                      <div className="rounded-2xl border border-carbon-geo p-6 bg-[#FAFDF7]" data-id="element-1584">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1585">
                          <div data-id="element-1586">
                            <p className="text-xs font-bold text-carbon-mid/60 uppercase tracking-wider mb-2" data-id="element-1587">
                              Certificate Issued
                            </p>
                            <p className="text-sm text-carbon-deep font-semibold" data-id="element-1588">
                              {program.certName}
                            </p>
                          </div>
                          <div data-id="element-1589">
                            <p className="text-xs font-bold text-carbon-mid/60 uppercase tracking-wider mb-2" data-id="element-1590">
                              Assessment
                            </p>
                            <p className="text-sm text-carbon-mid" data-id="element-1591">
                              Written examination + practical case study.
                              Minimum 70% pass mark.
                            </p>
                          </div>
                          <div data-id="element-1592">
                            <p className="text-xs font-bold text-carbon-mid/60 uppercase tracking-wider mb-2" data-id="element-1593">
                              Validity
                            </p>
                            <p className="text-sm text-carbon-mid" data-id="element-1594">
                              3 years. Annual CPD requirement of 8 hours for
                              renewal.
                            </p>
                          </div>
                          <div data-id="element-1595">
                            <p className="text-xs font-bold text-carbon-mid/60 uppercase tracking-wider mb-2" data-id="element-1596">
                              CPD Recognition
                            </p>
                            <p className="text-sm text-carbon-mid" data-id="element-1597">
                              {program.cpdHours} CPD hours. Recognized for HR
                              compliance reporting.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>}
            </div>

            {/* Right: Sticky enrollment panel */}
            <div className="lg:sticky lg:top-24" data-id="element-1598">
              <div className="rounded-2xl border border-carbon-geo bg-white overflow-hidden shadow-lg shadow-carbon-deep/5" data-id="element-1599">
                <div className="bg-carbon-deep p-5 rounded-t-2xl" data-id="element-1600">
                  <p className="text-xs font-bold uppercase tracking-widest text-carbon-bright mb-1" data-id="element-1601">
                    {program.shortTitle}
                  </p>
                  <p className="text-2xl font-extrabold text-white" data-id="element-1602">
                    ${program.price.toLocaleString()}
                  </p>
                  <p className="text-xs text-white/50 mt-0.5" data-id="element-1603">
                    per participant
                  </p>
                </div>

                <div className="p-5 space-y-3.5 border-b border-carbon-geo" data-id="element-1604">
                  <div className="flex items-center justify-between text-sm" data-id="element-1605">
                    <span className="text-carbon-mid flex items-center gap-2" data-id="element-1606">
                      <ClockIcon className="w-4 h-4" data-id="element-1607" /> Duration
                    </span>
                    <span className="font-bold text-carbon-deep" data-id="element-1608">
                      {program.duration}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm" data-id="element-1609">
                    <span className="text-carbon-mid flex items-center gap-2" data-id="element-1610">
                      <GraduationCapIcon className="w-4 h-4" data-id="element-1611" /> Level
                    </span>
                    <span className="font-bold text-carbon-deep" data-id="element-1612">
                      {program.level}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm" data-id="element-1613">
                    <span className="text-carbon-mid flex items-center gap-2" data-id="element-1614">
                      <AwardIcon className="w-4 h-4" data-id="element-1615" /> CPD Hours
                    </span>
                    <span className="font-bold text-carbon-deep" data-id="element-1616">
                      {program.cpdHours} hours
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm" data-id="element-1617">
                    <span className="text-carbon-mid flex items-center gap-2" data-id="element-1618">
                      <UsersIcon className="w-4 h-4" data-id="element-1619" /> Corporate Discount
                    </span>
                    <span className="font-bold text-carbon-bright" data-id="element-1620">
                      5+ seats
                    </span>
                  </div>
                </div>

                <div className="p-5 space-y-3" data-id="element-1621">
                  <a href={MAILTO.enrollCourse} className="flex items-center justify-center gap-2 w-full py-3.5 bg-carbon-bright text-white text-sm font-semibold rounded-lg hover:bg-carbon-light transition-colors shadow-sm shadow-carbon-bright/20" data-id="element-1622">
                    Enroll Now <ArrowRightIcon className="w-4 h-4" data-id="element-1623" />
                  </a>
                  <a href={MAILTO.courseSyllabus} className="flex items-center justify-center gap-2 w-full py-3 border-2 border-carbon-bright text-carbon-deep text-sm font-semibold rounded-lg hover:bg-carbon-bright/5 transition-colors" data-id="element-1624">
                    <DownloadIcon className="w-4 h-4" data-id="element-1625" /> Download Syllabus
                  </a>
                  <a href={MAILTO.corporateCohort} className="flex items-center justify-center gap-2 w-full py-3 border border-carbon-geo text-carbon-mid text-sm font-semibold rounded-lg hover:bg-[#FAFDF7] transition-colors" data-id="element-1626">
                    Request Corporate Cohort
                  </a>
                </div>

                <div className="px-5 pb-5" data-id="element-1627">
                  <p className="text-[10px] text-carbon-mid/60 text-center leading-relaxed" data-id="element-1628">
                    Corporate cohort pricing for 5+ participants. Contact us for
                    custom scheduling.
                  </p>
                </div>
              </div>

              <button onClick={() => navigate('courses')} className="mt-4 flex items-center gap-2 text-sm font-semibold text-carbon-mid hover:text-carbon-bright transition-colors w-full justify-center" data-id="element-1629">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-1630" />
                Back to all programs
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── OTHER PROGRAMS ── */}
      <section className="w-full bg-[#FAFDF7] py-16 border-t border-carbon-geo" data-id="element-1631">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1632">
          <FadeIn data-id="element-1633">
            <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-carbon-bright bg-carbon-bright/10 px-3 py-1 rounded-full mb-4" data-id="element-1634">
              Related Programs
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-carbon-deep mb-8" data-id="element-1635">
              Other Certification Programs
            </h2>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-1636">
            {programs.filter(p => p.id !== program.id).slice(0, 3).map((p, i) => <FadeIn key={p.id} delay={i * 0.06} data-id="element-1637">
                  <button onClick={() => navigate(p.route)} className="w-full text-left rounded-2xl border border-carbon-geo bg-white p-6 hover:border-carbon-bright/40 hover:shadow-md transition-all group" data-id="element-1638">
                    <div className="flex items-start gap-3 mb-3" data-id="element-1639">
                      <div className="w-10 h-10 rounded-xl bg-carbon-bright/10 flex items-center justify-center flex-shrink-0" data-id="element-1640">
                        <div className="text-carbon-bright" data-id="element-1641">{p.icon}</div>
                      </div>
                      <div data-id="element-1642">
                        <p className="text-[11px] font-bold uppercase tracking-widest text-carbon-bright" data-id="element-1643">
                          {p.category}
                        </p>
                        <p className="text-sm font-bold text-carbon-deep leading-snug" data-id="element-1644">
                          {p.shortTitle}
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-carbon-mid leading-relaxed mb-4" data-id="element-1645">
                      {p.duration} · {p.level} · {p.cpdHours} CPD hrs
                    </p>
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-carbon-bright group-hover:gap-2 transition-all" data-id="element-1646">
                      View Program <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-1647" />
                    </span>
                  </button>
                </FadeIn>)}
          </div>
        </div>
      </section>

      <Footer data-id="element-1648" />
    </>;
}